import { useCallback, useEffect, useState } from 'react';

/**
 * What a filter dropdown shows as selected.
 *
 * The grid captures a header's selected values when the header is created, so a
 * click never arrives back through props. This tracks the selection locally and
 * adopts the grid's copy whenever that changes, which is how switching match
 * mode swaps in the values belonging to the mode picked.
 */
export const useTableFilterSelection = (
  selectedValues: string[],
  selectionType: 'single' | 'multiple' = 'single'
) => {
  const [selected, setSelected] = useState<string[]>(selectedValues);

  // Serialised rather than joined: filter values contain spaces, so joining on
  // one makes ['New York'] and ['New', 'York'] the same key and the sync is
  // skipped.
  const incoming = JSON.stringify(selectedValues);
  useEffect(() => {
    setSelected(selectedValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incoming]);

  const toggle = useCallback(
    (value: string) => {
      setSelected((previous) => {
        if (previous.includes(value)) {
          return previous.filter((each) => each !== value);
        }

        return selectionType === 'multiple' ? [...previous, value] : [value];
      });
    },
    [selectionType]
  );

  const setSelection = useCallback((values: string[]) => {
    setSelected(values);
  }, []);

  return { selected, toggle, setSelection };
};
